import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"

import { logger } from "@repo/utils/logger"
import { getPersonalAccountQueryOptions } from "~/modules/personal-account/personal-account.queries"
import { createRolePermissionMethods } from "~/modules/roles-and-permissions/role-permission-methods"
import { getCurrentUserRolePermissionsQueryOptions } from "~/modules/roles-and-permissions/roles-and-permissions.queries"

const log = logger.child({
  module: "protected-layout",
})

export const Route = createFileRoute("/_protected-layout")({
  component: ProtectedLayout,
  beforeLoad: async ({ context, location }) => {
    if (!context.user) {
      throw redirect({
        to: "/auth/sign-in",
        search: {
          next: location.href,
        },
      })
    }

    // Make sure the user has verified their email address
    if (context.user.app_metadata.email_verified === false) {
      throw redirect({
        to: "/auth/otp",
        search: {
          type: "signup",
          email: context.user.email ?? "",
          next: location.href,
          token: "",
        },
      })
    }

    if (context.user.user_metadata?.requires_password_reset === true) {
      log.info("Redirecting user to update password", {
        userId: context.user.id,
      })
      throw redirect({
        to: "/auth/update-password",
        search: {
          next: location.href,
        },
      })
    }

    const rolePermissions = await context.queryClient.ensureQueryData(
      getCurrentUserRolePermissionsQueryOptions(context.user.id),
    )

    const permissionMethods = createRolePermissionMethods(
      context.appRole,
      rolePermissions,
    )

    // If the user is a resource and they are not onboarded, redirect them to the onboarding page
    // And this is not the `/resource-onboarding` route
    if (
      permissionMethods?.getIsResourceSystemUser() &&
      location.pathname !== "/resource-onboarding" &&
      !context.user.app_metadata.is_onboarded
    ) {
      throw redirect({
        to: "/resource-onboarding",
      })
    }

    return {
      rolePermissions,
    }
  },
  loader: async ({ context }) => {
    if (context.user) {
      context.queryClient
        .ensureQueryData(getPersonalAccountQueryOptions(context.user.id))
        .catch((error) => {
          log.error("Error getting personal account", { error })
          return null
        })

      // We await here to block the loader for the permissions query
      await context.queryClient.ensureQueryData(
        getCurrentUserRolePermissionsQueryOptions(context.user.id),
      )
    }
  },
})

function ProtectedLayout() {
  return <Outlet />
}
